// src/pages/Websites.js
import React, {useState, useEffect} from 'react';
import {
    Container,
    Row,
    Col,
    Button,
    Spinner,
    Alert,
    Form,
    InputGroup,
    Dropdown,
    Badge,
    Table,
    Card
} from 'react-bootstrap';
import WebsiteCard from '../components/Website/WebsiteCard';
import AddWebsiteForm from '../components/Forms/AddWebsiteForm';
import useWebsiteStore from '../store/websiteStore';
import {Modal, ConfirmModal} from '../components/UI/Modal';
import WebsiteSummary from "../components/Website/WebsiteSummary";

const Websites = () => {
    const {websites, fetchWebsites, removeWebsite, loading, error} = useWebsiteStore();
    const [showAddModal, setShowAddModal] = useState(false);
    const [websiteToRemove, setWebsiteToRemove] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState('name');

    useEffect(() => {
        fetchWebsites();
    }, [fetchWebsites]);

    const handleAddSuccess = () => {
        setShowAddModal(false);
    };

    const handleRemoveClick = (id) => {
        setWebsiteToRemove(id);
        setShowConfirmModal(true);
    };

    const handleRemoveConfirm = async () => {
        try {
            await removeWebsite(websiteToRemove);
            setShowConfirmModal(false);
            setWebsiteToRemove(null);
        } catch (error) {
            // Chyba je zpracována ve store
        }
    };

    // Filtrování a řazení webů
    const filteredWebsites = websites
        .filter(website => {
            // Filtrování podle vyhledávacího výrazu
            const searchMatch = website.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                website.url.toLowerCase().includes(searchTerm.toLowerCase());

            // Filtrování podle stavu
            const statusMatch = statusFilter === 'all' || website.status === statusFilter;

            return searchMatch && statusMatch;
        })
        .sort((a, b) => {
            // Řazení podle zvoleného kritéria
            switch (sortBy) {
                case 'name':
                    return (a.name || a.url).localeCompare(b.name || b.url);
                case 'status':
                    return a.status.localeCompare(b.status);
                case 'lastCheck':
                    return new Date(b.lastCheck) - new Date(a.lastCheck);
                default:
                    return 0;
            }
        });

    // Počítání webů podle stavu
    const statusCounts = websites.reduce((acc, website) => {
        acc[website.status] = (acc[website.status] || 0) + 1;
        return acc;
    }, {});

    return (
        <Container fluid>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <div>
                    <h1 className="mb-2">Moje weby</h1>
                    <div className="d-flex flex-wrap gap-2">
                        <Badge bg="primary" pill className="px-3 py-2">
                            Celkem: {websites.length}
                        </Badge>
                        <Badge bg="success" pill className="px-3 py-2">
                            Online: {statusCounts.online || 0}
                        </Badge>
                        <Badge bg="warning" pill className="px-3 py-2">
                            Problémy: {statusCounts.issues || 0}
                        </Badge>
                        <Badge bg="danger" pill className="px-3 py-2">
                            Offline: {statusCounts.offline || 0}
                        </Badge>
                    </div>
                </div>
                <Button
                    variant="primary"
                    onClick={() => setShowAddModal(true)}
                    className="align-self-md-start"
                >
                    <i className="fas fa-plus me-2"></i> Přidat web
                </Button>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" role="status" variant="primary">
                        <span className="visually-hidden">Načítání...</span>
                    </Spinner>
                </div>
            ) : websites.length === 0 ? (
                <div className="text-center py-5 bg-light rounded">
                    <div className="mb-3">
                        <i className="fas fa-globe fa-4x text-secondary"></i>
                    </div>
                    <h3>Žádné weby k monitorování</h3>
                    <p className="text-muted mb-4">Začněte přidáním prvního webu pro monitoring</p>
                    <Button
                        variant="primary"
                        onClick={() => setShowAddModal(true)}
                    >
                        <i className="fas fa-plus me-2"></i> Přidat web
                    </Button>
                </div>
            ) : (
                <>


                    <WebsiteSummary websites={websites}/>

                    <div className="d-flex flex-column flex-md-row gap-3 mb-4">
                        <InputGroup className="w-100 w-md-50">
                            <InputGroup.Text>
                                <i className="fas fa-search"></i>
                            </InputGroup.Text>
                            <Form.Control
                                placeholder="Vyhledat web..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <Button variant="outline-secondary" onClick={() => setSearchTerm('')}>
                                    <i className="fas fa-times"></i>
                                </Button>
                            )}
                        </InputGroup>

                        <div className="d-flex gap-2">
                            <Dropdown>
                                <Dropdown.Toggle variant="outline-secondary" id="status-filter-dropdown">
                                    <i className="fas fa-filter me-2"></i>
                                    {statusFilter === 'all' ? 'Všechny stavy' :
                                        statusFilter === 'online' ? 'Online' :
                                            statusFilter === 'issues' ? 'Problémy' :
                                                statusFilter === 'offline' ? 'Offline' : 'Filtr'}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => setStatusFilter('all')}
                                                   active={statusFilter === 'all'}>
                                        Všechny stavy
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setStatusFilter('online')}
                                                   active={statusFilter === 'online'}>
                                        <i className="fas fa-check-circle text-success me-2"></i> Online
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setStatusFilter('issues')}
                                                   active={statusFilter === 'issues'}>
                                        <i className="fas fa-exclamation-triangle text-warning me-2"></i> Problémy
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setStatusFilter('offline')}
                                                   active={statusFilter === 'offline'}>
                                        <i className="fas fa-times-circle text-danger me-2"></i> Offline
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>

                            <Dropdown>
                                <Dropdown.Toggle variant="outline-secondary" id="sort-dropdown">
                                    <i className="fas fa-sort me-2"></i>
                                    {sortBy === 'name' ? 'Seřadit: Název' :
                                        sortBy === 'status' ? 'Seřadit: Stav' :
                                            sortBy === 'lastCheck' ? 'Seřadit: Poslední kontrola' : 'Seřadit'}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => setSortBy('name')} active={sortBy === 'name'}>
                                        <i className="fas fa-font me-2"></i> Podle názvu
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setSortBy('status')} active={sortBy === 'status'}>
                                        <i className="fas fa-signal me-2"></i> Podle stavu
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setSortBy('lastCheck')}
                                                   active={sortBy === 'lastCheck'}>
                                        <i className="fas fa-clock me-2"></i> Podle poslední kontroly
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>


                    {filteredWebsites.length === 0 ? (
                        <Alert variant="info">
                            <i className="fas fa-info-circle me-2"></i>
                            Žádné weby neodpovídají zvoleným filtrům. Upravte vyhledávání nebo filtry.
                        </Alert>
                    ) : (
                        <>

                            <Row xs={1} sm={2} lg={3} xl={4} className="g-4 mb-4">
                                {filteredWebsites.map((website) => (
                                    <Col key={website.id}>
                                        <WebsiteCard
                                            website={website}
                                            onRemove={handleRemoveClick}
                                        />
                                    </Col>
                                ))}
                            </Row>

                            <Card className="mb-4 shadow-sm">
                                <Card.Header className="bg-white">
                                    <h5 className="mb-0">WordPress informace</h5>
                                </Card.Header>
                                <Card.Body>
                                    <Table responsive striped bordered hover>
                                        <thead>
                                        <tr>
                                            <th>Web</th>
                                            <th>PHP</th>
                                            <th>WordPress</th>
                                            <th>Šablona</th>
                                            <th>Pluginy k aktualizaci</th>
                                            <th>Poslední kontrola</th>
                                        </tr>
                                        </thead>
                                        <tbody>

                                        {filteredWebsites.filter((w)=>w.metaData?.wordpress_api_key).map((website) => (
                                            <tr key={website.id}>
                                                <td>{website.name}</td>
                                                <td>{website.metaData?.php?.version}
                                                    {website.metaData?.php?.version && website.metaData?.php?.version < 8 && (
                                                        <Badge
                                                            bg={"warning"}>8.2</Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    {website.metaData?.versions?.core.current_version}
                                                    {website.metaData?.versions?.core.latest_version && website.metaData?.versions?.core.latest_version != website.metaData?.versions?.core.current_version && (
                                                        <Badge
                                                            bg={"warning"}>{website.metaData?.versions?.core.latest_version}</Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    {website.metaData?.versions?.themes.name} {' '}
                                                    {website.metaData?.versions?.themes.current_version}
                                                    {website.metaData?.versions?.themes.latest_version && website.metaData?.versions?.themes.latest_version != website.metaData?.versions?.themes.current_version && (
                                                        <Badge
                                                            bg={"warning"}>{website.metaData?.versions?.themes.latest_version}</Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    {website.metaData?.versions?.plugins?.length > 0 && (
                                                        website.metaData?.versions?.plugins.filter((plugin) => plugin.latest_version && plugin.latest_version != plugin.current_version).length > 0 ?
                                                            website.metaData?.versions?.plugins.filter((plugin) => plugin.latest_version && plugin.latest_version != plugin.current_version).map((plugin) => (
                                                                <div key={plugin.slug}>
                                                                    {plugin.name} {' '}
                                                                    {plugin.current_version}
                                                                    <Badge
                                                                        bg={"warning"}>{plugin.latest_version}</Badge>
                                                                </div>
                                                            )) : (
                                                                <>
                                                                    -
                                                                </>
                                                            )
                                                    )}
                                                </td>
                                                <td>
                                                    {website.metaData?.wordpress_last_check ? (
                                                        new Date(website.metaData?.wordpress_last_check).toLocaleString()
                                                    ) : ("-")}
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>

                        </>
                    )}
                </>
            )}

            <Modal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                title="Přidat nový web"
                size="md"
            >
                <AddWebsiteForm onSuccess={handleAddSuccess}/>
            </Modal>

            <ConfirmModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleRemoveConfirm}
                title="Potvrzení odstranění"
                message="Opravdu chcete odstranit tento web? Tato akce je nevratná a odstraní všechna data spojená s tímto webem."
                confirmText="Ano, odstranit"
                cancelText="Zrušit"
                variant="danger"
            />
        </Container>
    );
};

export default Websites;